import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import {
  addFileFeedPostAtom,
  addPostCommentAtom,
  deleteFileFeedPostAtom,
  deletePostCommentAtom,
  filteredFileFeedPostsAtom,
  setFileFeedSearchAtom,
  toggleFileFeedPostLikeAtom,
  updateFileFeedPostAtom,
  updatePostCommentAtom,
} from 'atoms';
import ActivityFeed from 'components/activity-feed/ActivityFeed';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import { UploadProgress } from 'hooks/useFileUpload';
import useToast from 'hooks/useToast';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FC, useMemo, useState } from 'react';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetFilePermissionsQuery } from 'redux-store/api/file-cabinet';
import { IProject } from 'redux-store/api/projects';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';

interface IFileActivityFeed {
  fileId: string;
  fileName: string;
  project: IProject;
  onClose?: () => void;
  onFileUpload?: (files: File[]) => Promise<string[]>;
}

export const FileActivityFeed: FC<IFileActivityFeed> = ({
  fileId,
  fileName,
  project,
  onClose,
  onFileUpload: externalOnFileUpload,
}) => {
  const { data: userMe } = useGetMeQuery();
  const showToast = useToast();
  const [uploadedFiles, setUploadedFiles] = useState<UploadProgress[]>([]);
  const { companyId } = useAppSelector(authSelector);

  const createPost = useSetAtom(addFileFeedPostAtom(fileId));
  const updatePost = useSetAtom(updateFileFeedPostAtom);
  const deletePost = useSetAtom(deleteFileFeedPostAtom);
  const createComment = useSetAtom(addPostCommentAtom);
  const updateComment = useSetAtom(updatePostCommentAtom);
  const deleteComment = useSetAtom(deletePostCommentAtom);
  const togglePostLike = useSetAtom(toggleFileFeedPostLikeAtom);
  const [searchQuery, setSearchQuery] = useAtom(setFileFeedSearchAtom);

  const filteredPostsAtomParams = useMemo(() => ({ fileId, searchQuery }), [fileId, searchQuery]);
  const posts = useAtomValue(filteredFileFeedPostsAtom(filteredPostsAtomParams)) || [];
  const uploadPath = project?.id
    ? `companies/${companyId}/projects/${project.id}/fileFeed`
    : undefined;

  const handleCreatePost = async (post: Partial<IPost>) => {
    try {
      const role = userMe?.roles?.find(
        (role) => role.companyId?.toString() === companyId
      )?.jobTitle;

      await createPost({
        newPost: { ...post, itemName: fileName },
        user: {
          userId: userMe?.id?.toString() || '',
          displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
          avatarUrl: userMe?.avatar || '',
          role,
        },
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error creating post: ${error.message}`,
      });
    }
  };

  const handleUpdatePost = async (post: Partial<IPost>) => {
    try {
      await updatePost({ ...post, itemName: fileName });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error updating post: ${error.message}`,
      });
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      await deletePost(postId);
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error deleting post: ${error.message}`,
      });
    }
  };

  const handleCreateComment = async (comment: Partial<IPostComment>, parentPath: string) => {
    try {
      await createComment({
        data: {
          ...comment,
          createdBy: {
            userId: userMe?.id?.toString() || '',
            displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
            avatarUrl: userMe?.avatar || '',
          },
        },
        parentPath,
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error creating comment: ${error.message}`,
      });
    }
  };

  const handleUpdateComment = async (comment: Partial<IPostComment>) => {
    try {
      await updateComment(comment);
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error updating comment: ${error.message}`,
      });
    }
  };

  const handleTogglePostLike = async (postId: string) => {
    try {
      await togglePostLike({
        postId,
        fileId,
        userId: userMe?.id?.toString() || '',
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error liking post: ${error.message}`,
      });
    }
  };

  const onSetSearchQuery = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };

  const { data: filePermissionsData, isLoading: isLoadingPermissions } = useGetFilePermissionsQuery(
    { projectId: project?.id?.toString(), fileId },
    { skip: !project?.id || !fileId }
  );

  const mentionOptions: ICustomMentionData[] = useMemo(() => {
    if (!filePermissionsData) {
      return [];
    }

    return filePermissionsData
      .filter(({ id }) => id !== userMe?.id)
      .map((user) => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        firstName: user.firstName,
        lastName: user.lastName,
        avatar: user.avatar || '',
        email: user.email,
      }));
  }, [filePermissionsData]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #F3F4F9',
        opacity: 1,
        height: '100%',
        padding: '0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flex: 1,
          opacity: 1,
          gap: '16px',
          paddingBottom: '0px',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {/* Upload Progress Display */}
        {uploadedFiles.length > 0 && (
          <Box
            sx={{
              padding: '16px 30px',
              borderBottom: '1px solid #EDEFF7',
              backgroundColor: '#f8f9fa',
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#23252F',
                marginBottom: '8px',
              }}
            >
              Upload Progress
            </Typography>
            {uploadedFiles?.map((file) => (
              <Box
                key={file.fileId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '4px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#666',
                    flex: 1,
                    marginRight: '8px',
                  }}
                >
                  {file.fileName}
                </Typography>
                <Box
                  sx={{
                    width: '60px',
                    height: '4px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${file.progress}%`,
                      height: '100%',
                      backgroundColor: file.status === 'error' ? '#f44336' : '#4caf50',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: '#666',
                    marginLeft: '8px',
                    minWidth: '30px',
                  }}
                >
                  {file.progress}%
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <ActivityFeed
          posts={posts}
          mentionOptions={mentionOptions}
          searchQuery={searchQuery}
          uploadPath={uploadPath}
          isInModal
          onCreatePost={handleCreatePost}
          onUpdatePost={handleUpdatePost}
          onCreateComment={handleCreateComment}
          onDeletePost={handleDeletePost}
          onUpdateComment={handleUpdateComment}
          onTogglePostLike={handleTogglePostLike}
          onSetSearchQuery={onSetSearchQuery}
          feedContextType="FILE_FEED"
          feedContextItemId={fileId}
        />
      </Box>
    </Box>
  );
};
